import axios from 'axios';
import { AnyAction } from 'redux';

// Data layer
import { COMPLETE_VEHICLE } from 'data-layer-cr/dist/constants/products';
import {
  Quotation,
  QuotationCoverage,
  QuotationUnit,
  ServerResponse,
} from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';

import { defaultUnit } from 'data-layer-cr/dist/functions/quotations';
import { makeEmptyQuotation } from 'data-layer-cr/dist/models/Quotation';
import { makeEmptyTotal } from 'data-layer-cr/dist/models/Total';

// Request/Response types
import PricingPreviewRequest from 'src/models/requestTypes/PricingPreviewRequest';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';

// Network
import processRequest from 'src/services/processRequest';

// Request factory methods
import { makeEmpty } from 'src/models/Quotation/CarItemFromLicense';
import {
  makePricingCreationRequest,
  makeUpdatePricingRequest,
} from 'src/models/requestTypes/RequestFactory';

// funtions
import { replaceCoverageValues } from 'src/functions/coverageMethods';

// Redux
import { getLoggedRole } from 'data-layer-cr/dist/authentication';
import { Plan } from 'src/components/screens/Quotations/components/OptionPriceCard/SelectPlan';
import { AppThunk, AsyncAppThunk, AsyncDispatch } from '../../ThunkTypes';

// Actions
import {
  cleanCopyQuotationData,
  closeCopyQuotationModal,
  overwriteCurrentQuotation,
  startCopyQuotationData,
  updateCarItemFromLicense,
  updateCoveragePricing,
  updateCurrentProfile,
  updateCurrentQuotationChanges,
  updatePlans,
  updateQuotationModalIsOpen,
  updateTotalPricePreview,
  updateUnitCoverages,
  updateUsedAdditionalBroker,
  updateUsedLicense,
  updateUsedProfile,
  updateUsedSubAgentLicense,
} from '../slice/quotationSlice';

import {
  CREATE_QUOTATION,
  GET_PLANS_REQUEST,
  REQUEST_PRICING,
} from '../requestIdentifiers';

import {
  appliesNewAutoPxDiscount,
  makeQuotationCreationError,
  requestQuotationPricingByProfile,
  trackCreatedQuotationEvent,
  updateQuotationUnits,
} from './helperMethods';

/** Helpers */
export const UNIQUE_PRICING_REQUEST_ID = 'UNIQUE_PRICING_REQUEST_ID';
export const PAGINATION_STEP_SIZE = 12;
export const APPLICATION_NAME = 'salesManagerDashboard'; // TEMPORARY FIX, should be removed once the backend is updated/refactored.

const quotationPricePerUnitEndpoint = 'Quotation/priceperunit';

/**
 * Updates the quotation's pricing according to provided coverages
 *
 * @param newCoverages - The coverages to use to recalculate the quotation
 * @param deductibleOption - The selected option for the "dedictubles" section
 * @param product - The current selected product
 */
export const updatePricingForCoverages =
  (
    newCoverages: QuotationCoverage[],
    modifiedPackage: number,
    deductibleOption: number,
    product: string,
    shouldUpdate = true,
    packageSelected?: string,
  ): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    // Create the request
    const { currentCar } = getState().car;
    const { currentCustomer } = getState().customer;
    const { usedProfile, currentProfile } = getState().quotation;
    const { customer, summary, isAutoPx, user, units } =
      getState().quotation.currentQuotation;
    const requestData: PricingPreviewRequest = makeUpdatePricingRequest({
      car: currentCar,
      coverages: newCoverages,
      customer: currentCustomer,
      hasMultipleOptions: true,
      modifiedPackage,
      productId: product,
      units,
      deductOption: deductibleOption,
      profileId: usedProfile,
      packageSelected,
    });
    // Overwrite with the selected product
    requestData.productId = product;
    // Perform requests
    dispatch(setRequestStatus(REQUEST_PRICING, 'requesting'));
    try {
      const respQuotation = await requestQuotationPricingByProfile(
        requestData,
        currentProfile,
      );
      respQuotation.customer = customer;
      respQuotation.summary = summary;
      respQuotation.isAutoPx = isAutoPx;
      respQuotation.productId = product;
      respQuotation.user = {
        ...user,
        role: getLoggedRole(),
      };

      const appliesForNewAutoPxDiscount = appliesNewAutoPxDiscount(isAutoPx);

      if (appliesForNewAutoPxDiscount && product === COMPLETE_VEHICLE) {
        /** On create quotation is always just 1 unit */
        const autoPxUnits = await processRequest<
          ServerResponse<QuotationUnit[]>
        >({
          method: 'POST',
          url: quotationPricePerUnitEndpoint,
          data: respQuotation.units,
        });

        respQuotation.units = updateQuotationUnits(
          respQuotation.units,
          autoPxUnits.data,
        );
      }

      if (shouldUpdate) {
        dispatch(overwriteCurrentQuotation(respQuotation));
      } else {
        dispatch(
          updateTotalPricePreview(respQuotation.units[modifiedPackage].total),
        );
      }

      return dispatch(setRequestStatus(REQUEST_PRICING, 'success'));
    } catch (e) {
      if (axios.isCancel(e)) {
        return dispatch(setRequestStatus(REQUEST_PRICING, 'requesting'));
      }
      return dispatch(setRequestStatus(REQUEST_PRICING, 'failure'));
    }
  };

/**
 * Updates the current quotation with information comming from the package unit.
 * Make sure to only update the values related to the new price, namely coverages and total
 *
 * @param packageUnit - The package to use when displaying the price
 */
export const updatePricingForPackage =
  (packageUnit: QuotationUnit): AppThunk =>
  (dispatch, getState): void => {
    const { currentQuotation } = getState().quotation;
    const currentUnit = defaultUnit(currentQuotation);
    const newQuotation: Quotation = {
      ...currentQuotation,
      units: [
        {
          ...currentUnit,
          coverages: packageUnit.coverages,
          total: packageUnit.total,
        },
        // concatenate the rest of the units
        ...currentQuotation.units.filter(
          (unit) => unit.package.id !== currentUnit.package.id,
        ),
      ],
    };
    dispatch(overwriteCurrentQuotation(newQuotation));
  };

/**
 * Submits the quotation's values to the server, so that it can actually
 * save the quotation instance in the database.
 *
 * @param deductibleOption - Which deductible option was selected in the slider
 * @param product - Which product was selected
 */
export const makePricing =
  (
    deductibleOption: number,
    product: string,
    hasMultipleOptions: boolean,
    packageSelected = 1,
  ): AsyncAppThunk =>
  async (dispatch: AsyncDispatch, getState): Promise<AnyAction> => {
    const brandsCatalog = getState().catalog.brands;
    const modelsCatalog = getState().catalog.models;
    const { currentCustomer } = getState().customer;
    const car = getState().car.currentCar;
    const { customer, units } = getState().quotation.currentQuotation;
    const {
      usedLicense,
      usedProfile,
      usedAdditionalBroker,
      usedsubAgentLicense,
    } = getState().quotation;
    const fullCoverageList: QuotationCoverage[] =
      getState().quotation.currentQuotation.units[0].coverages;

    const payload = makePricingCreationRequest({
      customer: currentCustomer,
      car,
      productId: product,
      coverages: fullCoverageList,
      brands: brandsCatalog,
      models: modelsCatalog,
      license: usedLicense,
      packageSelected,
      deductibleOption,
      hasMultipleOptions,
      profileId: usedProfile,
      additionalBrokers: usedAdditionalBroker,
      units,
      subagentlicense: usedsubAgentLicense,
    });
    payload.productId = product;

    /** Copy quotation data */
    const { quotationId } = getState().quotation.copyQuotationData;
    payload.parentQuotation = quotationId;

    /**
     * Fallbacks in case summary information could not be generated. This will mainly happen when
     * trying to create a cloned quotation, as there is no brand/model list loaded
     */
    if (payload.summary.carBrand === '') {
      payload.summary.carBrand =
        getState().quotation.currentQuotation.summary.carBrand;
    }
    if (payload.summary.carModel === '') {
      payload.summary.carModel =
        getState().quotation.currentQuotation.summary.carModel;
    }

    dispatch(setRequestStatus(CREATE_QUOTATION, 'requesting'));
    try {
      const response = await processRequest<Quotation>({
        method: 'POST',
        url: 'Quotation/create',
        data: payload,
        customErrorMessage: makeQuotationCreationError,
      });

      response.customer = customer;

      /** This is a temporary solution, it will be replaced on sprint 68
       *  where this endpoint would always be called.
       */
      const quotation = response;
      const appliesForNewAutoPxDiscount = appliesNewAutoPxDiscount(
        quotation.isAutoPx,
      );

      if (appliesForNewAutoPxDiscount) {
        const autoPxUnits = await processRequest<
          ServerResponse<QuotationUnit[]>
        >({
          method: 'POST',
          url: quotationPricePerUnitEndpoint,
          data: quotation.units,
        });

        quotation.units = updateQuotationUnits(
          quotation.units,
          autoPxUnits.data,
        );
      }

      dispatch(overwriteCurrentQuotation(response));
      trackCreatedQuotationEvent(response, deductibleOption);
      return dispatch(setRequestStatus(CREATE_QUOTATION, 'success'));
    } catch {
      return dispatch(setRequestStatus(CREATE_QUOTATION, 'failure'));
    }
  };

/**
 * Prevents the previous quotation from leaking into the current quotation's detail
 * page.
 */
export const cleanCurrentQuotationReference =
  (): AppThunk =>
  (dispatch, getState): void => {
    dispatch(updateCurrentQuotationChanges([]));

    // Here we need to retain previous status of autoPx eligibility
    dispatch(
      overwriteCurrentQuotation({
        ...makeEmptyQuotation(),
        isAutoPx: getState().quotation.currentQuotation.isAutoPx,
      }),
    );
  };

/**
 * Toggles whether the quotation creation modal is open
 */
export const updateQuotationModalIsOpenValue =
  (isOpen: boolean): AppThunk =>
  (dispatch): void => {
    dispatch(updateQuotationModalIsOpen(isOpen));
  };

export const updateSelectedProduct =
  (newProduct: string): AppThunk =>
  (dispatch, getState): void => {
    dispatch(
      overwriteCurrentQuotation({
        ...getState().quotation.currentQuotation,
        productId: newProduct,
      }),
    );
  };

export const updateLicenseFormValues =
  (
    licenseCode: string,
    profileId: number,
    additionalBrokersValue: string[],
    currentProfile: PricingProfileResponse,
    subAgentValue: string,
  ): AppThunk =>
  (dispatch): void => {
    dispatch(updateUsedLicense(licenseCode));
    dispatch(updateUsedProfile(profileId));
    dispatch(updateCurrentProfile(currentProfile));
    dispatch(updateUsedAdditionalBroker(additionalBrokersValue));
    dispatch(updateUsedSubAgentLicense(subAgentValue));
  };

export const cleanCarDetailsFromLicense =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateCarItemFromLicense(makeEmpty()));
  };

export const cleanTotalPricePreview =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(updateTotalPricePreview({ ...makeEmptyTotal(), grandTotal: -1 }));
  };

export const updateQuotationPrices =
  (
    modifiedCoverages: QuotationCoverage[],
    selectedPackage: string,
    packageOption: number,
  ): AppThunk =>
  (dispatch, getState): void => {
    const { currentQuotation, totalPricePreview } = getState().quotation;
    const newCoverages = replaceCoverageValues(
      currentQuotation.units.find(
        (item) => item.package.description === selectedPackage,
      )!.coverages,
      modifiedCoverages,
    );
    dispatch(
      updateCoveragePricing({
        newCoverages,
        newTotal: totalPricePreview,
        packageSelected: selectedPackage,
        packageOption,
      }),
    );
  };

export const updateQuotationPricesWithNewCoverages =
  (
    selectedPackage: string,
    modifiedCoverages: QuotationCoverage[],
    packageOption: number,
  ): AppThunk =>
  (dispatch): void => {
    dispatch(
      updateUnitCoverages({
        packageSelected: selectedPackage,
        newCoverages: modifiedCoverages,
        packageOption,
      }),
    );
  };

export const cleanCopyQuotation =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(cleanCopyQuotationData());
  };

export const startCopyQuotation =
  (quotationHash: string, quotationId: number): AppThunk =>
  (dispatch): void => {
    dispatch(startCopyQuotationData({ quotationHash, quotationId }));
  };

export const closeCopyQuotationDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(closeCopyQuotationModal());
  };

export const getPlans =
  (profileId: number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(GET_PLANS_REQUEST, 'requesting'));
    try {
      const result = await processRequest<ServerResponse<Plan[]>>({
        method: 'GET',
        url: `Plans/profileId=${profileId}`,
      });

      dispatch(updatePlans(result.data));

      return dispatch(setRequestStatus(GET_PLANS_REQUEST, 'success'));
    } catch {
      return dispatch(setRequestStatus(GET_PLANS_REQUEST, 'failure'));
    }
  };
